import type { MoneyFormatter } from "@slot/game-shell";

/**
 * The paytable the info page shows at the current bet: slot-backend-platform's `packages/game-math-cannon/config/base.json`
 * (configHash bec8f760, read 2026-11-02), whose values are × the total bet for a count of matching symbols anywhere on
 * the 5 × 4 grid. A symbol pays its highest tier at or under its count (6–7, 8–10, 11 or more); Wilds count toward it,
 * and the cannon multiplies the win on top. Kept in step with the math by hand: a change there is a change here.
 */

export type PaytableTierSpec = {
  /** Lower bound on the matching-symbol count (inclusive). */
  minCount: number;
  /** Upper bound (inclusive). For the top tier this is the grid's size. */
  maxCount: number;
  /** Win multiplier applied to the total bet (e.g. 15 means win = bet × 15). */
  multiplier: number;
};

export type PaytableRowSpec = {
  /** Symbol identifier (the frame name in the symbols atlas). */
  symbolId: string;
  /** Per-tier specs in descending order (11+ first, then 8–10, then 6–7). */
  tiers: readonly PaytableTierSpec[];
};

/** The most matching symbols a grid can hold: 5 reels × 4 rows. */
const PAYTABLE_GRID_MAX_COUNT = 20;

const tiers = (top: number, middle: number, low: number): readonly PaytableTierSpec[] => [
  { minCount: 11, maxCount: PAYTABLE_GRID_MAX_COUNT, multiplier: top },
  { minCount: 8, maxCount: 10, multiplier: middle },
  { minCount: 6, maxCount: 7, multiplier: low },
];

/** The 9 paying symbols (H4–H1, L5–L1), in the page's order. */
export const PAYTABLE_SPEC: readonly PaytableRowSpec[] = [
  { symbolId: "H4", tiers: tiers(15, 7.5, 3) },
  { symbolId: "H3", tiers: tiers(9, 3.5, 1.25) },
  { symbolId: "H2", tiers: tiers(6, 2, 0.9) },
  { symbolId: "H1", tiers: tiers(4.5, 1.25, 0.6) },
  { symbolId: "L5", tiers: tiers(3.5, 0.7, 0.4) },
  { symbolId: "L4", tiers: tiers(2.5, 0.5, 0.3) },
  { symbolId: "L3", tiers: tiers(2, 0.45, 0.225) },
  { symbolId: "L2", tiers: tiers(1.5, 0.35, 0.15) },
  { symbolId: "L1", tiers: tiers(1, 0.25, 0.1) },
] as const;

/**
 * Builds the token map injected into the info-page HTML at parse time.
 *
 * One token per (symbolId, tier minCount) pair: `paytable.<symbol>.tier<N>`.
 * Values are formatted money strings (currency-aware) computed from the
 * current bet × the tier multiplier.
 *
 * Example output at bet "100" with USD formatter:
 * ```
 * {
 *   "paytable.H4.tier11": "$15.00",   // 100 × 15
 *   "paytable.H4.tier8":  "$7.50",
 *   "paytable.H4.tier6":  "$3.00",
 *   ...
 *   "paytable.L1.tier6":  "$0.10",
 * }
 * ```
 *
 * Consumed by `<td>{paytable.H4.tier11}</td>` references in the HTML.
 */
export function buildPaytableTokens(input: {
  betMinor: string;
  formatter: MoneyFormatter;
}): Record<string, string> {
  const tokens: Record<string, string> = {};
  for (const row of PAYTABLE_SPEC) {
    for (const tier of row.tiers) {
      const winMinor = paidMinor(input.betMinor, tier.multiplier);
      tokens[`paytable.${row.symbolId}.tier${tier.minCount}`] = input.formatter.formatMinor(winMinor);
    }
  }
  return tokens;
}

/**
 * A symbol win as the backend pays it: the bet × the paytable value, rounded half to even to whole minor units
 * (slot-backend-platform's money package; the cannon then multiplies the rounded win). At the lowest bets a value such as
 * 0.35 lands on a half: 10 minor × 0.35 is 3.5, paid as 4, where truncating would show 3.
 */
function paidMinor(betMinor: string, multiplier: number): string {
  const [whole, fraction = ""] = multiplier.toString().split(".");
  const scale = 10n ** BigInt(fraction.length);
  const product = BigInt(betMinor) * BigInt(`${whole}${fraction}`);
  const quotient = product / scale;
  const twiceRemainder = (product % scale) * 2n;
  if (twiceRemainder > scale || (twiceRemainder === scale && quotient % 2n === 1n)) return (quotient + 1n).toString();
  return quotient.toString();
}
